import React, { useEffect, useState } from 'react';
import { Form, Input, Select, DatePicker, InputNumber, Upload, Button, message, FormInstance, Rate, Divider, Radio, Space, TimePicker, Checkbox, Tooltip, Row, Col, ColorPicker } from 'antd';
import { ComboConfig, FormColumnConfig } from '../../interfaces/entity';
import './Style.scss';
import AsyncSelect from './components/asyncSelect';
import { getData, uploadFile } from '../../actions/apiCall';
import { useDispatch } from 'react-redux';
import { UploadOutlined } from '@ant-design/icons'
import DynamicSelect from '../Select/DynamicSelect';
import { decryptData } from '../../actions/crypto';
import ReactQuill from 'react-quill';
import "react-quill/dist/quill.bubble.css";
import { Editor } from '@monaco-editor/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import moment from 'moment';
dayjs.extend(utc);

interface RenderForms {
    column: FormColumnConfig,
    combo?: ComboConfig[],
    form?: FormInstance,
    editingField?: string | null,
    setEditingField?: (field: string | null) => void
}

const getPlaceholder = (column, type) => {
    if (column.placeholder) {
        return column.placeholder
    }
    // Check if label is a string before calling toLowerCase
    const labelText = typeof column?.label === 'string' ? column.label : 'field';
    switch (type) {
        case 'date':
            return `Select ${labelText.toLowerCase()}`
        case 'select':
            return `Select ${labelText.toLowerCase()}`
        default:
            return `Enter ${labelText.toLowerCase()}`
    }
}

const RenderFormItem: React.FC<RenderForms> = ({ column, combo, form }) => {
    const { type, children, rules, inputProps, checkboxProps, dateProps, reactQuillProps, inputNumberProps,
        inputTextAreaProps, rateProps, dynamicProps, checkBoxGroupProps, toolTipProps, timePickerProps, selectProps, radioProps,
        commonProps, colorProps, editorProps, formItem, ...rest } = column;
    const [fileList, setFileList] = useState([]);

    const dispatch = useDispatch();

    const formItemProps = {
        name: column.name,
        label: column.label,
        className: column.className,
        rules: column.rules,
        ...rest,
    };

    useEffect(() => {
        if (commonProps?._fileList?.length > 0) {
            setFileList(commonProps?._fileList);
        }
    }, [commonProps?._fileList]);

    const mockFetchOptions = async (page: number, search: string, dependencies?: Record<string, any>) => {
        let _combo: any = {};
        if (Array.isArray(column.name)) _combo = combo?.find(ele => ele.name == column.name[1]);
        else _combo = combo?.find(ele => ele.name == column.name);

        const { api, idKey, render, isEcrypted } = _combo ?? {}
        const pageSize = 20

        if (!api) {
            return { data: [], hasMore: false };
        }

        const apiResponse = await dispatch(getData(api, { current: page, pageSize: pageSize, search, ...dependencies }))
        if (isEcrypted) {
            apiResponse.data = decryptData(apiResponse.data);
        }

        if (apiResponse.code == 200) {
            const { pagination } = apiResponse;
            const data = apiResponse.data.map(ele => {
                const renderedLabel = render(ele, apiResponse.data);
                // Extract text label for selected value display
                let textLabel: string;
                if (typeof renderedLabel === 'string') {
                    textLabel = renderedLabel;
                } else {
                    // Try to extract name from common fields
                    const firstName = ele.firstName || '';
                    const middelName = ele.middelName || '';
                    const lastName = ele.lastName || '';
                    textLabel = [firstName, middelName, lastName].filter(Boolean).join(' ') ||
                        ele.name ||
                        ele.dealName ||
                        ele.vendorName ||
                        String(ele[idKey] ?? idKey(ele));
                }

                return {
                    value: ele[idKey] ?? idKey(ele),
                    label: textLabel, // Simple string for selected value display
                    optionLabel: renderedLabel, // React component for dropdown options
                    data: ele // Store original data for custom rendering   
                };
            })
            const hasMore = pagination ? pagination.pageSize < pageSize : false
            return { data, hasMore: !hasMore };
        } else {
            return { data: [], hasMore: false };
        }
    };


    const handleFileChange = ({ file, fileList }) => {
        setFileList([...fileList])
        if (file.status === 'done') {
            if (rest?.uploadProps?.multiple) {
                const uploadedUrls = fileList
                    .filter(f => f.status === 'done' && f.response?.url)
                    .map(f => f.response.url);

                form.setFieldsValue({ [column.name]: uploadedUrls });
            } else {
                const uploadedUrl = file.response?.url;
                form.setFieldsValue({ [column.name]: uploadedUrl });
            }
        }
    };


    const handleFileUpload = async (file, onSuccess, onError) => {
        const formData = new FormData();
        formData.append('file', file);
        try {
            const resData = await uploadFile('/upload/add', formData);
            if (resData.status === 'SUCCESS') {
                const fileUrl = resData.files[0]
                onSuccess({ url: fileUrl });
                message.success(`${file.name} uploaded successfully.`);
            } else {
                onError();
                message.error(`Failed to upload ${file.name}.`);
            }
        } catch (error) {
            message.error(`Error uploading ${file.name}: ${error.message}`);
        }
    };

    const toolbarOptions = [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"],
    ];

    const modules = {
        toolbar: toolbarOptions,
    };

    const customCheckBoxField = (e: FormColumnConfig) => {
        return <Row {...rest?.rowProps}>
            {e?.formItems.map((ele: FormColumnConfig) => <Col span={ele?.span} {...rest?.colProps}>
                <RenderFormItem column={ele} combo={combo} form={form} />
            </Col>)}
        </Row>
    }

    // If formItem is provided, use it instead of default rendering
    if (formItem) {
        // For custom type without a name (not a form field), render directly without Form.Item wrapper
        if (type === 'custom' && !column.name) {
            return formItem();
        }

        // For custom type with name, check if formItem expects form instance
        // If formItem returns a Form.Item itself, we should call it directly with formItemProps and form
        // Support both single parameter and two parameter signatures
        if (formItem.length === 2) {
            // formItem expects (formItemProps, form) - use type assertion to handle two parameters
            const formItemFn = formItem as ((props?: any, formInstance?: any) => React.ReactNode);
            return formItemFn(formItemProps, form);
        } else {
            // formItem expects only formItemProps, wrap in Form.Item to provide field props
            return (
                <Form.Item {...formItemProps}>
                    {(fieldProps: any) => {
                        // Merge formItemProps with actual field props (value, onChange, etc.) from Form.Item
                        const mergedProps = {
                            ...formItemProps,
                            value: fieldProps?.value,
                            onChange: fieldProps?.onChange || ((val: any) => {
                                form.setFieldValue(column.name, val);
                            }),
                        };
                        return formItem(mergedProps);
                    }}
                </Form.Item>
            );
        }
    }

    switch (type) {
        case 'text':
            return <Form.Item {...formItemProps}><Input {...inputProps} placeholder={getPlaceholder(column, type)} {...commonProps} /></Form.Item>;
        case 'email':
            return <Form.Item {...formItemProps}><Input {...inputProps} type='email' placeholder={getPlaceholder(column, type)} {...commonProps} /></Form.Item>;
        case 'url':
            return <Form.Item {...formItemProps}><Input {...inputProps} placeholder={getPlaceholder(column, type)} {...commonProps} /></Form.Item>;
        case 'textarea':
            return <Form.Item {...formItemProps}><Input.TextArea {...inputTextAreaProps} placeholder={getPlaceholder(column, type)} {...commonProps} /></Form.Item>;
        case 'date':
            return (
                <Form.Item {...formItemProps}>
                    <DatePicker
                        {...dateProps}
                        {...commonProps}
                        placeholder={getPlaceholder(column, type)}
                    />
                </Form.Item>
            );
        case 'time':
            return <Form.Item {...formItemProps}><TimePicker {...timePickerProps} placeholder={getPlaceholder(column, type)} {...commonProps} /></Form.Item>;
        case 'select':
            return (
                <Form.Item {...formItemProps}>
                    {column.isCombo ? <AsyncSelect
                        combo={combo?.filter(ele => ele.name == column.name)[0]}
                        column={{ ...column, extraOptions: column.extraOptions || [] }}
                        fetchOptions={(page, search) => {
                            if (formItemProps && formItemProps?.dependent) {
                                let filedName: any = formItemProps?.dependent(false);
                                if (Array.isArray(formItemProps?.name)) {
                                    filedName = formItemProps?.dependent(true, formItemProps?.name[0])
                                }
                                const dependentField = form.getFieldValue(filedName);
                                return mockFetchOptions(page, search, { dependentField: dependentField })
                            } else {
                                return mockFetchOptions(page, search, { dependentField: null })
                            }
                        }}
                        placeholder={getPlaceholder(column, type)}
                        {...selectProps}
                        {...commonProps}
                    /> :
                        <Select {...selectProps} placeholder={getPlaceholder(column, type)} {...commonProps}>
                            {children?.map((option) => (
                                <Select.Option key={option.name} value={option.value} disabled={option.disabled}>
                                    {option.name}
                                </Select.Option>
                            ))}
                        </Select>}
                </Form.Item>
            );
        case 'number':
            return <Form.Item {...formItemProps}><InputNumber {...inputNumberProps} placeholder={getPlaceholder(column, type)} {...commonProps} /></Form.Item>;
        case 'file':
            return <Form.Item {...formItemProps}>
                <Upload
                    customRequest={({ file, onSuccess, onError }) =>
                        handleFileUpload(file, onSuccess, onError)
                    }
                    onChange={handleFileChange}
                    fileList={fileList}
                    listType="text"
                    {...rest.uploadProps}
                    {...commonProps}
                >
                    {rest?.uploadProps?.listType != 'text' ?
                        (rest?.uploadProps?.maxCount == 1 && fileList.length == 0 || rest?.uploadProps?.maxCount !== 1) && <Button icon={<UploadOutlined />} />
                        : <Button icon={<UploadOutlined />}>Upload File</Button>}
                </Upload>
            </Form.Item>;
        case 'rate':
            return <Form.Item {...formItemProps}>
                <Rate {...rateProps} {...commonProps} />
            </Form.Item>
        case 'checkbox':
            return <Form.Item {...formItemProps} label={''} valuePropName="checked">
                <Checkbox {...checkboxProps}>
                    <Space direction='horizontal' size={[0, 0]}>
                        {formItemProps?.label}
                        {toolTipProps && <Tooltip {...toolTipProps} />}
                    </Space>
                </Checkbox>
            </Form.Item>
        case 'radio':
            return <Form.Item {...formItemProps}>
                <Radio.Group {...radioProps} {...commonProps}>
                    <Space direction={radioProps?.direction ?? "vertical"}>
                        {children?.map((option, index) => {
                            const keyValue = option.value !== undefined ? String(option.value) : `radio-${index}`;
                            return (
                                <Radio
                                    key={keyValue}
                                    value={option.value}
                                    disabled={option.disabled}
                                >
                                    {option.name}
                                </Radio>
                            );
                        })}
                    </Space>
                </Radio.Group>
            </Form.Item>
        case 'divider':
            return <Divider {...rest.dividerProps} >{column?.label}</Divider>
        case 'content':
            return <div>
                {column?.items}
            </div>
        case 'checkboxGroup':
            return <Form.Item {...formItemProps}>
                <Checkbox.Group {...checkBoxGroupProps} {...commonProps}>
                    <Space direction="vertical">
                        {children?.map((option) => (<Checkbox value={option.value}>{option.name}</Checkbox>))}
                    </Space>
                </Checkbox.Group>
            </Form.Item>
        case 'DynamicSelect':
            return <Form.Item {...formItemProps}>
                <DynamicSelect {...dynamicProps} {...selectProps} {...commonProps} />
            </Form.Item>
        case 'quill':
            return <Form.Item {...formItemProps}>
                <ReactQuill
                    theme="bubble"
                    style={{
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                        padding: "8px",
                        minHeight: "120px",
                    }}
                    modules={modules}
                    {...reactQuillProps}
                    {...commonProps}
                />
            </Form.Item>

        case 'customCheckBox':
            return customCheckBoxField(column)
        case 'color':
            return <Form.Item {...formItemProps}>
                <ColorPicker
                    onChange={(color) => form.setFieldsValue({ [column.name]: color.toHexString() })}
                    {...colorProps}
                    {...commonProps}
                />
            </Form.Item>
        case 'editor':
            return <Form.Item {...formItemProps}>
                <Editor
                    className="border-none h-full"
                    theme="vs-dark"
                    {...editorProps}
                    {...commonProps}
                />
            </Form.Item>
        default:
            // If formItem exists, call it (it may or may not accept parameters)
            if (formItem) {
                // For custom type without name, call without parameters
                if (type === 'custom' && !column.name) {
                    return formItem();
                }
                // Support both single parameter and two parameter signatures
                // Use type assertion to handle the two-parameter case
                const formItemFn = formItem as ((props?: any, formInstance?: any) => React.ReactNode);
                if (formItem.length === 2) {
                    return formItemFn(formItemProps, form);
                } else {
                    return formItemFn(formItemProps);
                }
            }
            return null;
    }
};



export default RenderFormItem;
